// import components
import { Box, Button } from '@mui/material';
import LoadingOverlay from 'components/shared/LoadingOverlay';
import PlanUnitEditableField from './PlanUnitEditableField';
import PlanUnitDetailsCategory from './PlanUnitDetailsCategory';
import { StyledPaper } from 'components/shared/Paper/StyledPaper.styles';
import { StyledPlanUnitDetailsBox } from './PlanUnitDetails.styles';

// import hooks
import { useTranslation } from 'react-i18next';
import useGetPlanUnitDetails from '../hooks/useGetPlanUnitDetails';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useGetCountriesQuery } from 'components/admin-portal/TravelDistances/queries';
import useSavePlanUnitDetails from '../hooks/useSavePlanUnitDetails';
import { useSelector } from 'hooks';

// import utils
import { schema } from './utils/schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { toOptions } from 'utils/toOptions';
import { getForm } from './utils/form';

// import types
import { PlanUnitDetails as PlanUnitDetailsType } from 'components/types';

const DarkButton = ({
  onClick,
  label,
  variant = 'contained',
  disabled
}: {
  onClick: () => void;
  label: string;
  variant?: 'outlined' | 'contained';
  disabled?: boolean;
}) => {
  return (
    <Button
      type="button"
      variant={variant}
      color="primary"
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </Button>
  );
};

export interface PlanUnitDetailsParams {
  planUnitId?: number;
  referenceDate?: Date | null;
  isEditMode: boolean;
  setIsEditMode: (isEditMode: boolean) => void;
}

const PlanUnitDetails = ({
  planUnitId,
  referenceDate,
  isEditMode,
  setIsEditMode
}: PlanUnitDetailsParams) => {
  const { t } = useTranslation('common', { keyPrefix: 'costCenters.details' });

  const {
    hasCostCenterCardIssues,
    hasCostCenterCardRemarks,
    hasCostCenterCardAgenciesRemarks,
    hasCostCenterCardEditDetails,
    hasPoolsOrgGrant
  } = useSelector((state) => state.grants);

  const { data: planUnitDetails, refetch } = useGetPlanUnitDetails(
    planUnitId,
    referenceDate
  );

  const { data: countries } = useGetCountriesQuery();

  const methods = useForm<PlanUnitDetailsType>({
    resolver: yupResolver(schema)
  });
  const { control, reset, handleSubmit } = methods;

  useEffect(() => {
    if (planUnitDetails) {
      reset({ ...planUnitDetails });
    }
  }, [planUnitDetails]);

  const { mutateAsync, isLoading } = useSavePlanUnitDetails(
    {
      planUnitId: planUnitId!,
      referenceDate: new Date(referenceDate!).toISOString()
    },
    undefined,
    ['PlanUnitDetails', planUnitId, referenceDate]
  );

  const saveChanges = async (formData: PlanUnitDetailsType) => {
    await mutateAsync(formData);
    await refetch();

    setIsEditMode(false);
  };

  const form = getForm({
    countryOptionBuilder: () => toOptions(countries!, 'code', 'name'),
    showIssues: hasCostCenterCardIssues,
    showEmployeeRemarks: hasCostCenterCardRemarks,
    showAgencyRemarks: hasCostCenterCardAgenciesRemarks,
    showPoolFields: hasPoolsOrgGrant
  });

  const formView = form.map((category) => (
    <PlanUnitDetailsCategory
      key={category.categoryName}
      categoryName={category.categoryName}
    >
      {category.fields.map((field) => (
        <PlanUnitEditableField
          key={field.objectKey}
          isEditMode={isEditMode && !field.blockEdit}
          control={control}
          planUnitDetails={planUnitDetails}
          objectKey={field.objectKey}
          langKey={field.langKey}
          fieldType={field.fieldType}
          optionsBuilder={field.optionsBuilder}
        />
      ))}
    </PlanUnitDetailsCategory>
  ));

  return (
    <>
      <LoadingOverlay loading={isLoading} />

      {hasCostCenterCardEditDetails && (
        <StyledPlanUnitDetailsBox>
          {!isEditMode ? (
            <DarkButton
              label={t('edit')}
              onClick={() => setIsEditMode(true)}
              disabled={!planUnitDetails || !countries}
            />
          ) : (
            <>
              <DarkButton
                label={t('cancel')}
                variant="outlined"
                onClick={() => setIsEditMode(false)}
              />

              <DarkButton
                label={t('save')}
                onClick={handleSubmit(saveChanges)}
              />
            </>
          )}
        </StyledPlanUnitDetailsBox>
      )}

      <FormProvider {...methods}>
        <StyledPaper>
          <Box display="flex" flexWrap="wrap" gap={4}>
            {formView}
          </Box>
        </StyledPaper>
      </FormProvider>
    </>
  );
};

export default PlanUnitDetails;
